import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, RefreshCw, Trash2, Edit3, ExternalLink, Wand2, AlertCircle, CheckCircle2, X, Power, PowerOff } from "lucide-react";
import { toast } from "sonner";
import client from "../lib/api";
import CategorySelect from "../components/CategorySelect";

const fmtDate = (iso) => {
  if (!iso) return "Never";
  const dt = new Date(iso);
  const diff = (Date.now() - dt.getTime()) / 1000;
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return dt.toLocaleString();
};

export default function SourcesPage() {
  const [sources, setSources] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const refresh = async () => {
    try {
      const r = await client.get("/admin/sources");
      setSources(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load sources");
    }
  };
  useEffect(() => { refresh(); }, []);

  const syncOne = async (s) => {
    setBusyId(s.id);
    try {
      const r = await client.post(`/admin/sources/${s.id}/sync`, null, { timeout: 600000 });
      const res = r.data.result;
      if (res.error) {
        toast.error(`Sync failed: ${res.error}`);
      } else {
        toast.success(`Synced ${s.name}: ${res.imported} new · ${res.skipped} already in library · ${res.failed} failed`);
      }
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusyId(null);
    }
  };
  const syncAll = async () => {
    if (sources.filter((s) => s.enabled).length === 0) return toast.error("No enabled sources to sync");
    setSyncingAll(true);
    try {
      const r = await client.post(`/admin/sources/sync-all`, null, { timeout: 1800000 });
      const total = r.data.results.reduce((acc, x) => acc + (x.imported || 0), 0);
      toast.success(`Autopilot complete · ${total} new transmissions imported across ${r.data.synced} sources`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSyncingAll(false);
    }
  };
  const remove = async (s) => {
    if (!window.confirm(`Remove source "${s.name}"? (Imported videos stay in your library.)`)) return;
    try {
      await client.delete(`/admin/sources/${s.id}`);
      toast.success("Source removed");
      refresh();
    } catch { toast.error("Failed to remove"); }
  };
  const toggle = async (s) => {
    await client.put(`/admin/sources/${s.id}`, { enabled: !s.enabled });
    refresh();
  };

  return (
    <div data-testid="sources-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">Autopilot</span>
          <h1 className="text-3xl font-black text-white tracking-tight">Channel Sources</h1>
          <p className="mt-1 text-zinc-500 text-sm max-w-xl">Subscribe Nexus to your TikTok / IG / YouTube handles. One click pulls every new public drop straight into your library — already deduped, categorized, and analytics-ready.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="sync-all-btn"
            onClick={syncAll}
            disabled={syncingAll || sources.length === 0}
            className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-zinc-900 border border-[#FF6B00]/40 text-[#FFB800] text-xs uppercase tracking-widest font-bold hover:border-[#FF6B00] hover:text-white disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${syncingAll ? "animate-spin" : ""}`} /> {syncingAll ? "Syncing..." : "Sync All"}
          </button>
          <button
            data-testid="add-source-btn"
            onClick={() => setShowAdd(true)}
            className="aura-button inline-flex items-center gap-2 h-10 px-5 rounded-full text-xs uppercase tracking-widest"
          >
            <Plus className="w-4 h-4" /> Add Source
          </button>
        </div>
      </div>

      {sources.length === 0 ? (
        <EmptyState onAdd={() => setShowAdd(true)} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="sources-grid">
          {sources.map((s) => (
            <SourceCard
              key={s.id}
              s={s}
              busy={busyId === s.id}
              onSync={() => syncOne(s)}
              onEdit={() => setEditing(s)}
              onRemove={() => remove(s)}
              onToggle={() => toggle(s)}
            />
          ))}
        </div>
      )}

      {(showAdd || editing) && (
        <SourceModal
          source={editing}
          onClose={() => { setShowAdd(false); setEditing(null); }}
          onSaved={() => { setShowAdd(false); setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div data-testid="sources-empty" className="rounded-2xl border border-dashed border-[#FF6B00]/30 bg-zinc-900/40 p-10 text-center">
      <Wand2 className="w-10 h-10 mx-auto text-[#FF6B00] aura-float" />
      <h3 className="mt-4 text-xl font-bold text-white">No sources yet</h3>
      <p className="mt-2 text-zinc-400 max-w-md mx-auto text-sm">Paste a creator handle URL and Nexus will pull every new post automatically. Works with TikTok, Instagram, YouTube channels, X/Twitter, Reddit, Vimeo and 1500+ sites.</p>
      <button onClick={onAdd} className="aura-button mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-full text-sm uppercase tracking-wider">
        <Plus className="w-4 h-4" /> Add Your First Source
      </button>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-left max-w-2xl mx-auto">
        {[
          { label: "TikTok", example: "https://www.tiktok.com/@thesaiyanvictoria" },
          { label: "Instagram", example: "https://www.instagram.com/thesaiyanvictoria" },
          { label: "YouTube", example: "https://www.youtube.com/@TheSaiyanVictoria" },
        ].map((ex) => (
          <div key={ex.label} className="rounded-lg bg-black/40 border border-white/5 p-3">
            <p className="text-[10px] uppercase tracking-widest text-[#FFB800] font-bold">{ex.label}</p>
            <p className="mt-1 text-xs text-zinc-500 font-mono break-all">{ex.example}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function SourceCard({ s, busy, onSync, onEdit, onRemove, onToggle }) {
  return (
    <div data-testid={`source-card-${s.id}`} className={`rounded-2xl border p-5 transition-colors ${s.enabled ? "border-white/5 bg-zinc-900/40" : "border-white/5 bg-zinc-900/20 opacity-70"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800] font-bold">{s.default_category}</span>
            {!s.enabled && <span className="text-[10px] uppercase tracking-widest text-zinc-500">Paused</span>}
          </div>
          <h3 className="mt-1 text-lg font-bold text-white tracking-tight truncate">{s.name}</h3>
          <a href={s.handle_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-[#FFB800] font-mono truncate max-w-full">
            <ExternalLink className="w-3 h-3" /> <span className="truncate">{s.handle_url}</span>
          </a>
        </div>
        <button onClick={onToggle} data-testid={`source-toggle-${s.id}`} title={s.enabled ? "Pause auto-sync" : "Resume auto-sync"} className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center border ${s.enabled ? "border-[#FFB800]/40 text-[#FFB800] bg-[#FFB800]/10" : "border-white/10 text-zinc-500"}`}>
          {s.enabled ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
        </button>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <Stat label="Total" value={s.total_imported} />
        <Stat label="Last Sync" value={s.last_sync_count} />
        <Stat label="Scan Up To" value={s.max_videos} />
      </div>

      {s.last_sync_error ? (
        <div className="mt-3 inline-flex items-start gap-1.5 text-[11px] text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span className="line-clamp-2">{s.last_sync_error}</span>
        </div>
      ) : s.last_sync_at ? (
        <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-zinc-500">
          <CheckCircle2 className="w-3.5 h-3.5 text-[#FFB800]" /> Last synced {fmtDate(s.last_sync_at)}
        </div>
      ) : (
        <div className="mt-3 text-[11px] text-zinc-500">Never synced — click Sync Now to pull the latest drops.</div>
      )}

      <div className="mt-4 flex items-center gap-2">
        <button
          data-testid={`sync-${s.id}`}
          onClick={onSync}
          disabled={busy}
          className="aura-button flex-1 h-10 px-4 rounded-full text-xs uppercase tracking-widest inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> {busy ? "Syncing..." : "Sync Now"}
        </button>
        <button data-testid={`edit-source-${s.id}`} onClick={onEdit} className="w-10 h-10 rounded-full border border-white/10 text-zinc-400 hover:text-white flex items-center justify-center"><Edit3 className="w-4 h-4" /></button>
        <button data-testid={`remove-source-${s.id}`} onClick={onRemove} className="w-10 h-10 rounded-full border border-red-500/30 text-red-400 hover:bg-red-500/10 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-black/40 border border-white/5 px-2 py-2">
      <p className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</p>
      <p className="text-lg font-black text-white">{value ?? "—"}</p>
    </div>
  );
}

function SourceModal({ source, onClose, onSaved }) {
  const editing = !!source;
  const [name, setName] = useState(source?.name || "");
  const [handleUrl, setHandleUrl] = useState(source?.handle_url || "");
  const [category, setCategory] = useState(source?.default_category || "POV");
  const [type, setType] = useState(source?.default_type || "auto");
  const [maxVideos, setMaxVideos] = useState(source?.max_videos || 10);
  const [tags, setTags] = useState((source?.tags || []).join(", "));
  const [enabled, setEnabled] = useState(source ? source.enabled : true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim() || !handleUrl.trim()) return toast.error("Name and URL required");
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        handle_url: handleUrl.trim(),
        default_category: category.trim() || "General",
        default_type: type,
        max_videos: parseInt(maxVideos, 10) || 10,
        tags: tags.split(",").map((s) => s.trim()).filter(Boolean),
        enabled,
      };
      if (editing) {
        await client.put(`/admin/sources/${source.id}`, body);
        toast.success("Source updated");
      } else {
        await client.post(`/admin/sources`, body);
        toast.success("Source added — hit Sync Now to pull");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="source-modal" onClick={onClose} className="fixed inset-0 z-[60] bg-black/80 backdrop-blur flex items-center justify-center p-4">
      <div onClick={(e) => e.stopPropagation()} className="rounded-2xl bg-[#0a0a0a] border border-white/10 max-w-lg w-full p-6 fade-up max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">Autopilot</span>
            <h3 className="text-lg font-bold text-white">{editing ? "Edit Source" : "Add Channel Source"}</h3>
          </div>
          <button data-testid="source-modal-close" onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="mt-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Display Name</label>
            <input data-testid="source-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. SaiyanVictoria · TikTok" className="mt-1 w-full h-11 px-4 bg-zinc-900/70 border border-white/10 focus:border-[#FF6B00] outline-none rounded-lg text-white text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Handle / Channel URL</label>
            <input data-testid="source-url" value={handleUrl} onChange={(e) => setHandleUrl(e.target.value)} placeholder="https://www.tiktok.com/@thesaiyanvictoria" className="mt-1 w-full h-11 px-4 bg-zinc-900/70 border border-white/10 focus:border-[#FF6B00] outline-none rounded-lg text-white text-sm font-mono" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Default Category</label>
              <div className="mt-1"><CategorySelect value={category} onChange={setCategory} testid="source-category" /></div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Default Type</label>
              <div className="mt-1 grid grid-cols-3 gap-1">
                {["auto", "short", "long"].map((t) => (
                  <button key={t} type="button" onClick={() => setType(t)} data-testid={`source-type-${t}`} className={`h-11 rounded-lg text-[11px] uppercase tracking-widest border ${type === t ? "bg-[#FF6B00] text-black border-[#FF6B00] font-bold" : "bg-zinc-900/70 text-zinc-400 border-white/10"}`}>{t}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Scan up to (latest N)</label>
              <input data-testid="source-max" type="number" min="1" max="50" value={maxVideos} onChange={(e) => setMaxVideos(e.target.value)} className="mt-1 w-full h-11 px-4 bg-zinc-900/70 border border-white/10 rounded-lg text-white text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Tags (comma)</label>
              <input data-testid="source-tags" value={tags} onChange={(e) => setTags(e.target.value)} className="mt-1 w-full h-11 px-4 bg-zinc-900/70 border border-white/10 rounded-lg text-white text-sm" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <input type="checkbox" data-testid="source-enabled" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="w-4 h-4 accent-[#FF6B00]" />
            Enable auto-sync
          </label>
        </div>
        <button data-testid="source-save" onClick={submit} disabled={busy} className="aura-button mt-5 w-full h-11 rounded-full text-sm uppercase tracking-wider disabled:opacity-50">
          {busy ? "Saving..." : (editing ? "Save Changes" : "Add Source")}
        </button>
      </div>
    </div>
  );
}
